import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useHistory } from 'react-router-dom';
import { useCallback } from 'react';

import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import {
  getUserAccountTypeIsInternal,
  getUserProfileIsTest,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import {
  getIsPlanErknmStatusInProcess,
  getIsPlanErknmStatusUnderConsideration,
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusApproved,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ListTypeEnum } from 'APIPackage/CatalogsModule/enums/ListTypeEnum';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { ROUTE_FEEDBACK, ROUTE_KNM_LIST_BY_ID_AND_TYPE } from 'CorePackage/MainModule/routes/routes';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { getPlanData, getPlanDataErknmStatus, getPlanWatchOnlyByTestUser } from '../selectors/planSelectors';
import { planExportPlanById, planExportXmlForSign } from '../actions/planActions';
import { feedbackReset, feedbackSetPlanData } from '../../../FeedbackPackage/MainModule/actions/feedbackActions';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';

interface IPlanHeaderDropdownProps {
  handleOpenSignModal: () => void;
  handleUploadButtonClick: () => void;
  handleOpenSignInfoModal: () => void;
  handleAddKnmButtonClick: () => void;
  handleOpenSignUploadModal: () => void;
  handleOpenChangeKnoModal: () => void;
}

interface IPlanHeaderDropdownStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  plan: IKnmPlan | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  isInternal: boolean;
  isWatchOnlyByTestUser: boolean;
  isTestUser: boolean;
  isErknm: boolean;
}

const PlanHeaderDropdownWithoutStore = ({
  handleOpenSignModal,
  handleUploadButtonClick,
  handleOpenSignInfoModal,
  handleAddKnmButtonClick,
  handleOpenSignUploadModal,
  handleOpenChangeKnoModal,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  plan,
  planErknmStatus,
  isInternal,
  isWatchOnlyByTestUser,
  isTestUser,
  isErknm,
}: IPlanHeaderDropdownProps & IPlanHeaderDropdownStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();

  const isPlanErknmStatusInProcess: boolean = getIsPlanErknmStatusInProcess(planErknmStatus);
  const isPlanErknmStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(planErknmStatus);
  const isPlanErknmStatusConsidered: boolean = getIsPlanErknmStatusConsidered(planErknmStatus);
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);

  const isPlanSigned: boolean = !!plan?.knmPlanData?.signedXlsxFile?.signed;

  const canExportXmlForSign: boolean = isErknm && isSupervisor && !isPlanSigned && !isWatchOnlyByTestUser;

  const exportPlanById: (format: ExportFormatsEnum, id: number, approved?: boolean, considered?: boolean) => void = (
    format: ExportFormatsEnum,
    id: number,
    approved?: boolean,
    considered?: boolean,
  ) => dispatch(planExportPlanById(format, id, approved, considered));

  const handleExportPlan = (
    format: ExportFormatsEnum,
    id?: number | string,
    approved?: boolean,
    considered?: boolean,
  ) => () => {
    if (id !== undefined) {
      exportPlanById(format, Number(id), approved, considered);
    }
  };

  const handleExportXmlForSign = useCallback(() => {
    if (plan?.id !== undefined) {
      dispatch(planExportXmlForSign(Number(plan.id)));
    }
  }, [dispatch, plan?.id]);

  const toMainItems: IDropdownItem[] = [
    {
      id: 'to-main-export-ods',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id)} isDisabled={plan?.deleted}>
          Экспорт полного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-main-export-xlsx',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id)} isDisabled={plan?.deleted}>
          Экспорт полного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toExportXmlForSign: IDropdownItem[] = [
    {
      id: 'to-export-xml-for-sign',
      content: (
        <DropdownItem onClick={handleExportXmlForSign} isDisabled={plan?.deleted}>
          Экспорт плана для подписания
        </DropdownItem>
      ),
    },
  ];

  const toSign: IDropdownItem[] = [
    {
      id: 'divider-to-sign',
      isDivider: true,
    },
    {
      id: 'to-sign-browser',
      content: (
        <DropdownItem id="signButton" onClick={handleOpenSignModal} isDisabled={plan?.deleted}>
          Подписать в браузере
        </DropdownItem>
      ),
    },
  ];

  if (!isTestUser) {
    toSign.push({
      id: 'to-sign-upload',
      content: (
        <DropdownItem onClick={handleOpenSignUploadModal} isDisabled={plan?.deleted}>
          Загрузить подпись
        </DropdownItem>
      ),
    });
  }

  const navigateToTotalKnms = useCallback(() => {
    history.push(ROUTE_KNM_LIST_BY_ID_AND_TYPE(plan?.id || '', ListTypeEnum.TOTAL));
  }, [history, plan?.id]);

  const problemsRoute = useCallback(() => {
    dispatch(feedbackReset());
    history.push(ROUTE_FEEDBACK);
    if (plan) {
      dispatch(feedbackSetPlanData(plan));
    }
  }, [dispatch, plan, history]);

  const toTotalKnms: IDropdownItem[] = [
    {
      id: 'to-total-knms',
      content: <DropdownItem onClick={navigateToTotalKnms}>Просмотр полного состава плана</DropdownItem>,
    },
  ];

  const navigateToAgreedKnms = useCallback(() => {
    history.push(ROUTE_KNM_LIST_BY_ID_AND_TYPE(plan?.id || '', ListTypeEnum.AGREED));
  }, [history, plan?.id]);

  const toAgreedKnms: IDropdownItem[] = [
    {
      id: 'to-agreed-knms',
      content: <DropdownItem onClick={navigateToAgreedKnms}>Просмотр согласованного плана</DropdownItem>,
    },
  ];

  const toSignInfo: IDropdownItem[] = [
    {
      id: 'to-sign-info',
      content: <DropdownItem onClick={handleOpenSignInfoModal}>Информация о подписи</DropdownItem>,
    },
  ];

  const toAddKnm: IDropdownItem[] = [
    {
      id: 'to-add-knm',
      content: (
        <DropdownItem onClick={handleAddKnmButtonClick} isDisabled={plan?.deleted}>
          Добавить КНМ
        </DropdownItem>
      ),
    },
  ];

  const toAddAdjustments: IDropdownItem[] = [
    {
      id: 'to-add-adjustments',
      content: (
        <DropdownItem onClick={handleUploadButtonClick} isDisabled={plan?.deleted}>
          Загрузить корректировки
        </DropdownItem>
      ),
    },
  ];

  const toExportConsidered: IDropdownItem[] = [
    {
      id: 'to-add-considered-ods',
      content: (
        <DropdownItem
          onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id, false, true)}
          isDisabled={plan?.deleted}
        >
          Экспорт рассмотренного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-add-considered-xlsx',
      content: (
        <DropdownItem
          onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id, false, true)}
          isDisabled={plan?.deleted}
        >
          Экспорт рассмотренного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toExportApproved: IDropdownItem[] = [
    {
      id: 'to-add-approved-ods',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.ODS, plan?.id, true)} isDisabled={plan?.deleted}>
          Экспорт утвержденного плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'to-add-approved-xlsx',
      content: (
        <DropdownItem onClick={handleExportPlan(ExportFormatsEnum.XLSX, plan?.id, true)} isDisabled={plan?.deleted}>
          Экспорт утвержденного плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const toChangeKno: IDropdownItem = {
    id: 'to-change-kno',
    content: <DropdownItem onClick={handleOpenChangeKnoModal}>Изменить КНО</DropdownItem>,
  };

  const planProblem: IDropdownItem[] = [
    {
      id: 'planProblem',
      content: <DropdownItem onClick={problemsRoute}>Проблема с планом КНМ</DropdownItem>,
    },
  ];

  const dropdownItems = () => {
    let res: IDropdownItem[] = [];

    if (!isWatchOnlyByTestUser) {
      if (isSupervisor && isPlanErknmStatusInProcess) {
        res = [...res, ...toAddKnm];
      } else if (isSupervisor && isPlanErknmStatusApproved) {
        res = [...res, ...toAddKnm];
      }
    }
    if (isSupervisor && isPlanErknmStatusApproved && !isWatchOnlyByTestUser) {
      res = [...res, toChangeKno];
    }
    if (!isPlanErknmStatusUnderConsideration) {
      res = [...res, ...toTotalKnms];
    } else {
      res = [...res, ...toAgreedKnms];
    }
    if (plan?.knmPlanData?.signedXlsxFile?.signed && !isPlanErknmStatusApproved) {
      res = [...res, ...toSignInfo];
    }
    if (
      (isPlanErknmStatusInProcess || isPlanErknmStatusConsidered) &&
      !isWatchOnlyByTestUser &&
      !isProsecutorOrSuperProsecutor
    ) {
      res = [...res, ...toSign];
    }
    if (
      ((isSupervisor && isPlanErknmStatusInProcess && isInternal) ||
        (isProsecutorOrSuperProsecutor && isPlanErknmStatusUnderConsideration)) &&
      !isWatchOnlyByTestUser &&
      !isErknm
    ) {
      res = [...res, ...toAddAdjustments];
    }
    if (isPlanErknmStatusConsidered) {
      res = [...res, ...toExportConsidered];
    }
    if (isPlanErknmStatusApproved) {
      res = [...res, ...toExportApproved];
    }
    if (canExportXmlForSign) {
      res = [...res, ...toExportXmlForSign];
    }
    res = [...res, ...toMainItems];
    res = [...res, ...planProblem];

    return res;
  };

  return <Dropdown items={dropdownItems()} />;
};

const mapsStateToProps = (state: IRootState): IPlanHeaderDropdownStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  plan: getPlanData(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  isInternal: getUserAccountTypeIsInternal(state),
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
  isTestUser: getUserProfileIsTest(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const PlanHeaderDropdown = connect(mapsStateToProps)(PlanHeaderDropdownWithoutStore);
